import { Info, ChevronDown, ChevronUp, TriangleAlert, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import _ from 'lodash';
import { default as React, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import { connect } from 'react-redux';
import Select, { components } from 'src/components/ui/BrandSelect';
import * as yup from 'yup';
import { Actions as AUTHENTICATION_ACTIONS } from '../../store/actions/authenticationActions';
import { Button } from '../ui/button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { itemToOptions } from '../utils/GlobalFunctions';
import { whiteLabelSelect } from '../utils/ReactSelectStyles';
import ValidationMessage from '../utils/ValidationMessage';

const CustomMultiValue = (props) => {
  const VALUE_LIMIT = 3;
  const { index, getValue } = props;
  const hiddenLength = getValue().length - VALUE_LIMIT;

  return index < VALUE_LIMIT ? (
    <components.MultiValue {...props} />
  ) : index === VALUE_LIMIT ? (
    <div className='option-label'>{`+ ${hiddenLength} selected`}</div>
  ) : null;
};

const CustomOption = (props) => {
  let disabled = false;
  props.options.map((group) => {
    if (group.label === 'Unavailable Organisations') {
      for (const option of group.options) {
        if (option.label === props.label) {
          if (option.isDisabled) {
            disabled = true;
          }
        }
      }
    }
  });
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: disabled === true ? '#D9D9D9' : 'white',
        color: disabled === true ? '#5E5E5E' : 'black',
      }}>
      <components.Option {...props}>
        <input type='checkbox' checked={props.isSelected} disabled={disabled} onChange={() => null} />{' '}
        <label>{props.label}</label>
      </components.Option>
    </div>
  );
};

const validateUserSchema = yup.object().shape({
  firstName: yup.string().required('First Name is required'),
  lastName: yup.string().required('Last Name is required'),
  email: yup.string().email().required('Email is required'),
  selectedPermissions: yup.array().min(1, 'Please select at least one permission').required('Permissions are required'),
});

const InviteUserForm = ({
  inviteNewUser,
  cyfAdmin,
  setShowInviteUserForm,
  showInviteUserForm,
  whiteLabelAdminOrgs,
  consultantAdminOrgs,
  permissions,
  orgManager,
}) => {
  const defaultStructure = {
    organisation: { view: true, edit: false, manage: false },
    organisationMetrics: { view: true, create: false, edit: false, delete: false },
    facilities: { '*': { view: true, edit: true, delete: true } },
    facilityMetrics: { view: true, create: true, edit: true, delete: true },
    reports: { view: true, create: true },
    usageData: { view: true, create: true, edit: true, delete: true },
    dashboard: { view: true, create: true, edit: true, delete: true },
    developer: { read: false, write: false },
  };
  const permissionsStructure = {
    organisation: { view: false, edit: false, manage: false },
    organisationMetrics: { view: false, create: false, edit: false, delete: false },
    facilities: { '*': { view: false, edit: false, delete: false } },
    facilityMetrics: { view: false, create: false, edit: false, delete: false },
    reports: { view: false, create: false },
    usageData: { view: false, create: false, edit: false, delete: false },
    dashboard: { view: false, create: false, edit: false, delete: false },
    developer: { read: false, write: false },
  };
  const [user, setUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: 0,
    role: '',
    whiteLabelAdmin: null,
    consultantAdmin: null,
    permissions: defaultStructure,
  });
  const [showWLAdminField, setShowWLAdminField] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [validationError, setValidationError] = useState([]);
  const generalUserOption = permissions.find((opt) => opt.label === 'General User');
  const [selectedPermissions, setSelectedPermissions] = useState(generalUserOption ? [generalUserOption] : []);

  const updateField = (e) => {
    const editedUser = { ...user };
    editedUser[e.target.name] = e.target.value;
    setUser(editedUser);
  };

  const handleInviteUser = async () => {
    try {
      setProcessing(true);
      await validateUserSchema.validate({ ...user, selectedPermissions }, { abortEarly: false });
      await inviteNewUser(user);
      setShowInviteUserForm(false);
    } catch (error) {
      console.log('Error inviting user', error);
      if (error?.inner) {
        setValidationError(error.inner);
      }
    } finally {
      setProcessing(false);
    }
  };

  const updatePermissionsOnChange = (selectedOptions, permissionsObject, permissionsStructure) => {
    // Reset all permissions to false based on the full structure
    const resetPermissions = (structure, targetObject) => {
      Object.entries(structure).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          targetObject[key] = {};
          value.forEach((perm) => {
            targetObject[key][perm] = false; // Reset to false
          });
        } else if (typeof value === 'object') {
          targetObject[key] = {};
          resetPermissions(value, targetObject[key]); // Recursive reset
        } else if (typeof value === 'boolean') {
          targetObject[key] = false; // Reset boolean permissions
        }
      });
    };

    // Apply selected permissions
    const applyPermissions = (permissionsStructure, targetObject) => {
      Object.entries(permissionsStructure).forEach(([key, subPermissions]) => {
        if (Array.isArray(subPermissions)) {
          targetObject[key] = targetObject[key] || {};
          subPermissions.forEach((perm) => {
            targetObject[key][perm] = true; // Set to true based on selection
          });
        } else if (typeof subPermissions === 'object') {
          targetObject[key] = targetObject[key] || {};
          applyPermissions(subPermissions, targetObject[key]); // Recursive application
        } else if (subPermissions === true) {
          targetObject[key] = true; // Set boolean permissions
        }
      });
    };

    // Step 1: Reset all permissions
    resetPermissions(permissionsStructure, permissionsObject);

    // Step 2: Loop through selected options to apply true values
    selectedOptions.forEach((option) => {
      applyPermissions(option.value, permissionsObject);
    });

    // Step 3: Ensure facilities[*].view is true if Usage Data Access is selected but Facility Access is not
    const hasUsageDataAccess = selectedOptions.some((opt) => opt.label === 'Usage Data Access');
    const hasFacilityAccess = selectedOptions.some((opt) => opt.label === 'Facility Access');
    if (hasUsageDataAccess && !hasFacilityAccess) {
      if (permissionsObject && permissionsObject.facilities && permissionsObject.facilities['*']) {
        permissionsObject.facilities['*'].view = true;
      }
    }
  };

  const handleClose = () => setShowInviteUserForm(false);

  const selectPortalProps =
    typeof document !== 'undefined'
      ? { menuPortalTarget: document.body, menuPosition: 'fixed' }
      : {};

  const handlePermissionsChange = (selectedOptions) => {
    if (!selectedOptions) {
      setUser({
        ...user,
        permissions: permissionsStructure,
      });
      setSelectedPermissions([]);
      return;
    } else {
      setSelectedPermissions(selectedOptions);
      const permissionsObject = user?.permissions || {};
      updatePermissionsOnChange(selectedOptions, permissionsObject, permissionsStructure);
      setUser({ ...user, permissions: permissionsObject });
    }
  };

  return (
    <Dialog open={showInviteUserForm} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent
        onClose={handleClose}
        className='flex max-h-[90vh] max-w-2xl flex-col gap-0 overflow-hidden p-0 font-body'
        style={{ overflow: 'visible' }}>
        <DialogHeader className='shrink-0 space-y-0 border-b border-border px-6 pb-4 pt-6 pr-14 text-left'>
          <DialogTitle className='font-display text-lg'>Invite New User</DialogTitle>
        </DialogHeader>

        <DialogBody className='min-h-0 flex-1 overflow-y-auto px-6 py-5'>
          <div className='mx-auto flex w-full max-w-md flex-col'>
            <div>
              <Input
                id='firstName'
                type='text'
                name='firstName'
                onChange={updateField}
                placeholder='First Name *'
                className='mt-1 h-10 w-full'
              />
              <ValidationMessage
                message={_.find(validationError, { path: 'firstName' })?.message}
                style={{ marginBottom: 0 }}
              />
            </div>

            <div>
              <Input
                id='surname'
                type='text'
                name='lastName'
                onChange={updateField}
                placeholder='Surname *'
                className='mt-4 h-10 w-full'
              />
              <ValidationMessage
                message={_.find(validationError, { path: 'lastName' })?.message}
                style={{ marginBottom: 0 }}
              />
            </div>

            <div>
              <Input
                id='role'
                type='text'
                name='role'
                onChange={updateField}
                placeholder='Job Title'
                className='mt-4 h-10 w-full'
              />
            </div>

            <div className='input-with-icon-block relative mt-4'>
              <Input
                id='email'
                type='email'
                name='email'
                onChange={updateField}
                placeholder='Email (case sensitive) *'
                className='h-10 w-full pr-10'
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type='button'
                      className='absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-primary hover:bg-muted'
                      aria-label='Email format help'>
                      <Info size={16} strokeWidth={1.5} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side='left'
                    className='max-w-xs border border-border bg-popover text-primary shadow-md'>
                    <span className='text-xs leading-relaxed'>
                      Email addresses are case sensitive throughout the application. Please enter your email exactly as you wish
                      to use it going forward. You will receive a confirmation of registration when this process is completed,
                      please retain this email for reference to your registered email ID.
                    </span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <ValidationMessage message={_.find(validationError, { path: 'email' })?.message} style={{ marginBottom: 0 }} />
            </div>

            <div className='my-4 w-full [&_.react-tel-input]:w-full'>
              <PhoneInput
                country={'gb'}
                inputProps={{ name: 'phoneNumber' }}
                placeholder='Phone Number *'
                inputClass='inputClassInviteUser'
                onChange={(phoneNumber) => {
                  const editedUser = { ...user };
                  editedUser['phoneNumber'] = phoneNumber;
                  setUser(editedUser);
                }}
              />
            </div>

            <div className='w-full'>
              <div>
                {orgManager && (
                  <button
                    type='button'
                    id='show-org-manager'
                    className='mt-2 inline-flex cursor-pointer items-center gap-1 text-xs font-bold text-foreground'
                    onClick={() => setShowPermissions(!showPermissions)}>
                    {!showPermissions ? (
                      <span className='inline-flex items-center gap-1'>
                        Set Specific Permissions <ChevronDown size={16} strokeWidth={1.5} />
                      </span>
                    ) : (
                      <span className='inline-flex items-center gap-1'>
                        Set Specific Permissions <ChevronUp size={16} strokeWidth={1.5} />
                      </span>
                    )}
                  </button>
                )}

                <div className={orgManager && showPermissions ? 'mb-2 mt-2 block w-full' : 'hidden'}>
                  <Select
                    id='permissions-dropdown'
                    options={permissions}
                    value={selectedPermissions}
                    {...selectPortalProps}
                    styles={{
                      option: (base) => ({
                        ...base,
                        height: '100%',
                      }),
                    }}
                    isClearable={true}
                    isMulti={true}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    placeholder='User Permissions *'
                    onChange={handlePermissionsChange}
                    components={{
                      MultiValue: (props) => <CustomMultiValue {...props} />,
                      Option: CustomOption,
                    }}
                  />
                  <ValidationMessage
                    message={_.find(validationError, { path: 'selectedPermissions' })?.message}
                    style={{ marginBottom: 0 }}
                  />
                </div>
              </div>

              {cyfAdmin && (
                <button
                  type='button'
                  id='show-white-label'
                  className='mt-3 inline-flex cursor-pointer items-center gap-1 text-xs font-bold text-foreground'
                  onClick={() => setShowWLAdminField(!showWLAdminField)}>
                  {!showWLAdminField ? (
                    <span className='inline-flex items-center gap-1'>
                      <TriangleAlert size={16} strokeWidth={1.5} /> Consultant or White Label Admin? Click here
                    </span>
                  ) : (
                    <span>Close</span>
                  )}
                </button>
              )}
            </div>

            <div className={cyfAdmin && showWLAdminField ? 'mt-4 block space-y-3' : 'hidden'}>
              <Select
                id='white-label-admin-dropdown'
                options={whiteLabelAdminOrgs}
                value={whiteLabelAdminOrgs.find((org) => org.value === user['whiteLabelAdmin']) || null}
                styles={whiteLabelSelect}
                isClearable={true}
                placeholder='White Label Admin'
                {...selectPortalProps}
                onChange={(selected) => {
                  const editedUser = { ...user };
                  if (!selected) {
                    editedUser['whiteLabelAdmin'] = null;
                    editedUser['portalPermissions'] = null;
                  } else {
                    editedUser['whiteLabelAdmin'] = selected.value;
                    if (selected.value === '621784') {
                      editedUser['portalPermissions'] = { portal: ['VIEW_ADMIN', 'CYF_ADMIN'] };
                    } else {
                      editedUser['portalPermissions'] = { portal: ['VIEW_ADMIN', 'WHITELABEL'] };
                    }
                  }
                  setUser({ ...editedUser, consultantAdmin: null });
                }}
              />
              <Select
                id='consultant-dropdown'
                options={consultantAdminOrgs}
                value={consultantAdminOrgs.find((org) => org.value === user['consultantAdmin']) || null}
                isClearable={true}
                styles={{ ...whiteLabelSelect, control: (provided) => ({ ...provided, marginTop: 15 }) }}
                placeholder='Consultant Admin'
                {...selectPortalProps}
                onChange={(selected) => {
                  const editedUser = { ...user };
                  if (!selected) {
                    editedUser['consultantAdmin'] = null;
                    editedUser['portalPermissions'] = null;
                  } else {
                    editedUser['whiteLabelAdmin'] = null;
                    editedUser['consultantAdmin'] = selected.value;
                    editedUser['portalPermissions'] = { portal: ['VIEW_ADMIN', 'CONSULTANT'] };
                  }
                  setUser(editedUser);
                }}
              />
            </div>

            <div className='mt-4'>
              <Alert className='flex items-center'>
                <AlertDescription>
                  <b>
                    Please kindly note that new user invitations expire 1 week after being sent. Invitee must login and reset
                    their password within that window.
                  </b>
                </AlertDescription>
              </Alert>
            </div>
          </div>
        </DialogBody>

        <DialogFooter className='shrink-0 flex-row flex-wrap items-center justify-end gap-2 border-t border-border bg-muted/30 px-6 py-4 sm:flex-nowrap'>
          <Button type='button' variant='outline' onClick={handleClose}>
            Cancel
          </Button>
          <Button id='submit-button' disabled={processing} onClick={handleInviteUser}>
            {processing && <Loader2 size={16} strokeWidth={1.5} className='animate-spin icon-spinner mr-2' />}
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const mapStateToProps = (state) => {
  const allOrgs = state.admin?.organisations;
  const cyfAdmin = state.profile?.details?.cyfAdmin;

  let whiteLabelAdminOrgs, consultantAdminOrgs;

  const permissions = [
    {
      label: 'Organisation Manager',
      value: {
        organisation: { view: true, edit: true, manage: true },
        organisationMetrics: { view: true, create: true, edit: true, delete: true },
        facilities: { '*': { view: true, edit: true, delete: true } },
        facilityMetrics: { view: true, create: true, edit: true, delete: true },
        reports: { view: true, create: true },
        usageData: { view: true, create: true, edit: true, delete: true },
        dashboard: { view: true, create: true, edit: true, delete: true },
        developer: { read: true, write: true },
      },
    },
    {
      label: 'General User',
      value: {
        organisation: { view: true, edit: false, manage: false },
        organisationMetrics: { view: true, create: false, edit: false, delete: false },
        facilities: { '*': { view: true, edit: true, delete: true } },
        facilityMetrics: { view: true, create: true, edit: true, delete: true },
        reports: { view: true, create: true },
        usageData: { view: true, create: true, edit: true, delete: true },
        dashboard: { view: true, create: true, edit: true, delete: true },
        developer: { read: false, write: false },
      },
    },
    {
      label: 'Facility Access',
      value: {
        organisation: { view: false, edit: false, manage: false },
        organisationMetrics: { view: false, create: false, edit: false, delete: false },
        facilities: { '*': { view: true, edit: true, delete: true } },
        facilityMetrics: { view: true, create: true, edit: true, delete: true },
        reports: { view: false, create: false },
        usageData: { view: false, create: false, edit: false, delete: false },
        dashboard: { view: false, create: false, edit: false, delete: false },
        developer: { read: false, write: false },
      },
    },
    {
      label: 'Reports Access',
      value: {
        organisation: { view: false, edit: false, manage: false },
        organisationMetrics: { view: false, create: false, edit: false, delete: false },
        facilities: { '*': { view: false, edit: false, delete: false } },
        facilityMetrics: { view: false, create: false, edit: false, delete: false },
        reports: { view: true, create: true },
        usageData: { view: false, create: false, edit: false, delete: false },
        dashboard: { view: false, create: false, edit: false, delete: false },
        developer: { read: false, write: false },
      },
    },
    {
      label: 'Usage Data Access',
      value: {
        organisation: { view: false, edit: false, manage: false },
        organisationMetrics: { view: false, create: false, edit: false, delete: false },
        facilities: { '*': { view: false, edit: false, delete: false } },
        facilityMetrics: { view: false, create: false, edit: false, delete: false },
        reports: { view: false, create: false },
        usageData: { view: true, create: true, edit: true, delete: true },
        dashboard: { view: false, create: false, edit: false, delete: false },
        developer: { read: false, write: false },
      },
    },
    {
      label: 'View All',
      value: {
        organisation: { view: true, edit: false, manage: false },
        organisationMetrics: { view: true, create: false, edit: false, delete: false },
        facilities: { '*': { view: true, edit: false, delete: false } },
        facilityMetrics: { view: true, create: false, edit: false, delete: false },
        reports: { view: true, create: false },
        usageData: { view: true, create: false, edit: false, delete: false },
        dashboard: { view: true, create: false, edit: false, delete: false },
        developer: { read: false, write: false },
      },
    },
    {
      label: 'Developer',
      value: {
        organisation: { view: false, edit: false, manage: false },
        organisationMetrics: { view: false, create: false, edit: false, delete: false },
        facilities: { '*': { view: false, edit: false, delete: false } },
        facilityMetrics: { view: false, create: false, edit: false, delete: false },
        reports: { view: false, create: false },
        usageData: { view: false, create: false, edit: false, delete: false },
        dashboard: { view: false, create: false, edit: false, delete: false },
        developer: { read: true, write: true },
      },
    },
  ];

  if (cyfAdmin) {
    whiteLabelAdminOrgs = allOrgs && allOrgs.filter((org) => org.id === org.whiteLabelId).map(itemToOptions);
    whiteLabelAdminOrgs && whiteLabelAdminOrgs.push({ label: 'Compare Your Footprint', value: '621784' });
    consultantAdminOrgs = allOrgs && allOrgs.filter((org) => org.id === org.consultantId).map(itemToOptions);
  }

  let orgManager = state?.organisation?.permissions?.organisation?.manage || false;

  return {
    cyfAdmin,
    whiteLabelAdminOrgs: _.sortBy(whiteLabelAdminOrgs, ['label']),
    consultantAdminOrgs: _.sortBy(consultantAdminOrgs, ['label']),
    permissions,
    orgManager,
  };
};

const mapDispatchToProps = (dispatch) => ({
  inviteNewUser: (user) => dispatch(AUTHENTICATION_ACTIONS.inviteNewUser(user)),
});

export default connect(mapStateToProps, mapDispatchToProps)(InviteUserForm);
